import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { overflowHyphens } from '@hrworks/design-system/utilities';

import { Link } from '../../i18n/navigation';
import { getContrastFontColor } from '../../util/getContrastFontColor';
import { mq } from '../../util/mediaQueries';

/** Hoehe der sticky Leiste ab lg. Auf kleineren Geraeten ist der Footer nicht sticky (wie im Legacy-Portal). */
const STICKY_BAR_HEIGHT = 44;

/**
 * Solange der Footer gerendert ist: Die Hoehe der sticky Leiste als CSS-Variable (nutzt BackToTop,
 * damit der Button nicht auf der Leiste liegt) und als scroll-padding, damit per Tab oder Anker
 * angesteuerte Elemente nicht hinter der Leiste verschwinden.
 */
const stickyBarGlobals = css({
  [mq['>=lg']]: {
    ':root': {
      '--stellenportal-sticky-footer-height': `${STICKY_BAR_HEIGHT}px`,
    },
    html: {
      scrollPaddingBottom: 'var(--stellenportal-sticky-footer-height)',
    },
  },
});

const footerColors = (theme: Theme) => ({
  // Relativ zur Kundenschrift (RootLayout), damit deren Groesse weiter wirkt.
  fontSize: '0.875em',
  backgroundColor: theme.stellenportal?.headerStyle?.color || theme.eclipseTier2Color.surface.raised,
  color: theme.stellenportal?.headerStyle?.color
    ? getContrastFontColor(theme.stellenportal?.headerStyle?.color)
    : theme.eclipseTier2Color.text.default,
});

/**
 * Oberer Teil: Rechtliches und "Bereitgestellt von". Ab lg sticky am unteren Bildschirmrand; am
 * Seitenende liegt die Leiste wieder an ihrem Platz ueber dem unteren Teil. Muss ein Geschwister des
 * unteren Teils sein (nicht darin verschachtelt), sonst kann sticky den Footer nicht verlassen.
 */
const StickyBar = styled.footer<{ $standalone: boolean }>(({ theme, $standalone }) => [
  overflowHyphens,
  footerColors(theme),
  {
    borderTop: `solid 1px ${theme.eclipseTier2Color.border.bold}`,
    padding: `${theme.eclipseTier1.spacing['24px']} ${theme.eclipseTier1.spacing['24px']} ${
      $standalone ? theme.eclipseTier1.spacing['24px'] : theme.eclipseTier1.spacing['12px']
    }`,
    [mq['>=lg']]: {
      paddingInline: theme.eclipseTier1.spacing['48px'],
      position: 'sticky',
      bottom: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      minHeight: STICKY_BAR_HEIGHT,
      paddingBlock: 0,
    },
  },
]);

/** Unterer Teil: Social-Icons. Nicht sticky. */
const SocialBar = styled.div(({ theme }) => [
  footerColors(theme),
  {
    padding: `0 ${theme.eclipseTier1.spacing['24px']} ${theme.eclipseTier1.spacing['16px']}`,
    [mq['>=lg']]: {
      paddingInline: theme.eclipseTier1.spacing['48px'],
    },
  },
]);

const Inner = styled.div(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  margin: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.eclipseTier1.spacing['16px'],
  padding: `0 ${theme.eclipseTier1.spacing['16px']}`,
  [mq.isSmallDevice]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const SocialInner = styled.div(({ theme }) => ({
  maxWidth: 1100,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: theme.eclipseTier1.spacing['12px'],
  padding: `0 ${theme.eclipseTier1.spacing['16px']}`,
  [mq.isSmallDevice]: {
    alignItems: 'flex-start',
  },
}));

/**
 * Auf kleinen Geraeten liegt BackToTop (fixed, rechts unten) am Seitenende auf Hoehe der Icons.
 * Rechts Platz freihalten: der Button belegt 72px ab Rand, die Reihe endet ohne Zusatz bei 40px.
 */
const IconsRow = styled.div({
  maxWidth: '100%',
  [mq.isSmallDevice]: {
    paddingRight: 40,
  },
});

const Links = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.eclipseTier1.spacing['12px'],
  [mq.isSmallDevice]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const Separator = styled.span({
  [mq.isSmallDevice]: {
    display: 'none',
  },
});

const FooterLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.stellenportal?.headerStyle?.color
    ? getContrastFontColor(theme.stellenportal?.headerStyle?.color)
    : theme.eclipseTier2Color.text.default,
}));

const PoweredBy = styled.a({
  textDecoration: 'none',
  color: 'inherit',
  textTransform: 'uppercase',
  fontWeight: 500,
});

const Hr = styled.hr(({ theme }) => ({
  height: 1,
  margin: 0,
  border: 'none',
  backgroundColor: theme.eclipseTier2Color.border.bold,
  width: '100%',
}));

export const S = {
  stickyBarGlobals,
  StickyBar,
  SocialBar,
  Inner,
  SocialInner,
  IconsRow,
  Links,
  Separator,
  FooterLink,
  PoweredBy,
  Hr,
} as const;
